import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { CirclePlus } from "lucide-react";
import RadioButton from "../../../../components/UI/PageInput/RadioButton";
import useDebounce from "../../../../CustomHooks/useDebounce";
import {
  REGISTERED_CYLINDER,
  All_STATION_NAME,
  CHECK_CYLINDER,
} from "../../../../api/allApi_List";
import SearchDropdown from "../../../../components/UI/PageInput/SearchDropdown";
import CustomInput from "../../../../components/UI/PageInput/CustomInput";
import DateInput from "../../../../components/UI/PageInput/DateInput";
import GrayButton from "../../../../components/UI/buttonUI/GrayButton";
import usePostData from "../../../../CustomHooks/useAxios/usePostData";
import { prepareFormData } from "../../../../functions/prepareFormData";
import DialogBox from "../../../../components/UI/DialogBox";
import { handleErrMessage } from "../../../../functions/handleErrMessage";
import CylinderViewDialog from "../../dialog/CylinderViewDialog";
import useGetData from "../../../../CustomHooks/useAxios/useGetData";
import PrimaryButton from "../../../../components/UI/buttonUI/PrimaryButton";

const sizeOptions = [
  { id: 100, name: "100kg" },
  { id: 900, name: "900kg" },
];

const SingleRegistrationForm = () => {
  const { data, isLoaded, error, getData } = useGetData();
  useEffect(() => {
    getData(All_STATION_NAME);
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
    reset,
  } = useForm();
  const {
    executePost: registerCylinder,
    loading: registerLoading,
    formError: registerError,
    successResponse: registerResponse,
    setFormError: setRegisterFormError,
  } = usePostData();

  const {
    executePost: checkSerialNumber,
    loading: checkSerialLoading,
    formError: checkSerialErr,
    successResponse: serialNumberExist,
    setSuccessResponse: setSerialNumberExist,
    setFormError: setCheckSerialErr,
  } = usePostData();

  const received_date = watch("received_in_factory_at");
  const serialNumber = watch("serial_number");
  const debouncedQuery = useDebounce(serialNumber, 500);

  const handleClear = () => {
    setSerialNumberExist(null);
    setCheckSerialErr("");
    reset();
  };

  const onSubmit = (data) => {
    if (checkSerialLoading) return;

    if (serialNumber && ["true", "discarded"].includes(serialNumberExist)) {
      return;
    }
    const formData = prepareFormData(data);
    registerCylinder(formData, REGISTERED_CYLINDER, handleClear);
  };

  useEffect(() => {
    if (debouncedQuery) {
      const formData = new FormData();
      formData.append("serial_number", serialNumber);
      checkSerialNumber(formData, CHECK_CYLINDER);
    }
    if (debouncedQuery === "") {
      setSerialNumberExist(null);
    }
  }, [debouncedQuery]);

  return (
    <>
      {isLoaded && !error ? (
        <form
          className="2xs:max-lg:pt-0"
          onSubmit={
            !registerLoading ? (e) => handleSubmit(onSubmit)(e) : undefined
          }
        >
          <div className="flex flex-wrap 2xs:max-md:pt-2">
            <div className="inputLeftCol">
              <CustomInput
                type="text"
                name="serial_number"
                labelText="Cylinder No.*"
                placeholder="Enter"
                register={{
                  ...register("serial_number", {
                    required: "Please enter a cylinder no.",
                    validate: (value) => {
                      if (value && value.trim() === "") {
                        return "Cylinder No. cannot contain only spaces";
                      }
                      if (value !== value.trim()) {
                        return "Please remove spaces at the beginning or end";
                      }
                      if (value.includes(".")) {
                        return "Cylinder No. Name cannot include a period (.)";
                      }
                      return true;
                    },
                  }),
                }}
                error={errors.serial_number}
              />
              {/*=====================Cylinder Checking Error Messages===============*/}
              {checkSerialLoading && !checkSerialErr ? (
                <p className={`${cyErrorStyles.warning}`}>
                  Checking Cylinder No.
                </p>
              ) : checkSerialErr ? (
                <p className={`${cyErrorStyles.error}`}>{checkSerialErr}</p>
              ) : serialNumberExist === "true" && !errors.serial_number ? (
                <p className={`${cyErrorStyles.error}`}>
                  This cylinder number already exists!
                </p>
              ) : serialNumberExist === "discarded" && !errors.serial_number ? (
                <p className={`${cyErrorStyles.error}`}>
                  This cylinder number already exists and discarded!
                </p>
              ) : null}
              {/*========================================================================*/}
            </div>
            <div className="inputRightCol">
              <SearchDropdown
                name="size"
                label="Size *"
                keyName="name"
                id="id"
                options={sizeOptions}
                setValue={setValue}
                control={control}
                selectedVal={watch("size")}
                error={errors.size}
              />
            </div>
          </div>
          <div className="sm:flex">
            <div className="w-full sm:pr-1">
              <DateInput
                label="Received In Factory At"
                name={"received_in_factory_at"}
                value={received_date}
                setValue={setValue}
                register={{
                  ...register("received_in_factory_at"),
                }}
                disableFutureDays={true}
              />
            </div>
            <div className="w-full sm:pl-1">
              <SearchDropdown
                name="station_id"
                label="Site Name"
                keyName="name"
                id="id"
                options={data}
                setValue={setValue}
                control={control}
                selectedVal={watch("station_id")}
                search={true}
                showOptionId={true}
                disableValidationMessage={true}
              />
            </div>
          </div>
          <RadioButton
            label="Is Filled *"
            name="is_filled"
            options={["Filled", "Empty"]}
            control={control}
            required
            error={errors.is_filled}
          />
          <div className="complaintBtnWrap">
            <p className="pr-2">
              <PrimaryButton
                btnText={!registerLoading ? "Register" : "Adding..."}
                loading={registerLoading}
                Icon={CirclePlus}
                size={18}
              />
            </p>
            <p className="pl-2">
              <GrayButton btnText="Clear" width="w-40" onClick={handleClear} />
            </p>
          </div>
        </form>
      ) : error ? (
        <DialogBox
          title="Error"
          message={handleErrMessage(error)}
          isErrorHandleButtons={true}
        />
      ) : (
        <p className="cy-load-txt">Loading...</p>
      )}
      {registerError && (
        <DialogBox
          title="Error"
          btnText="Ok"
          message={handleErrMessage(registerError)}
          closeAction={() => setRegisterFormError("")}
        />
      )}
      {registerResponse && (
        <CylinderViewDialog viewDetails={registerResponse} />
      )}
    </>
  );
};

export default SingleRegistrationForm;

const cyErrorStyles = {
  warning: "text-orange font-figtree text-xs -mt-[14px]",
  error: "text-red-500 font-figtree text-xs -mt-[14px]",
};
